import type { RunAttemptResponse, RunDiagnosticsResponse, StartDetectionRunRequest } from '@prerender/diagnostics-api';
import { skipToken } from '@reduxjs/toolkit/query';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import api from '../../api/apiSlice';
import { isConflict } from './incidentsErrorMessage';
import type { RequestError } from './loadable';
import { runsApi, useGetLatestRunAttemptQuery, useGetRunAttemptQuery, useStartDetectionRunMutation } from './runsApi';

export const RUN_POLLING_INTERVAL_MS = 2000;

export type WatchedRunNotice =
  | { kind: 'result'; report: RunDiagnosticsResponse; forced: boolean }
  | { kind: 'unfinished'; forced: boolean }
  | { kind: 'overrunning'; startedAt: string; forced: boolean }
  | { kind: 'refused'; error: RequestError };

export type DetectionRun = {
  running: boolean;
  notice: WatchedRunNotice | undefined;
  start: (request: StartDetectionRunRequest) => void;
};

const NOT_POLLING = 0;

const isFinished = (attempt: RunAttemptResponse | undefined): boolean =>
  attempt !== undefined && attempt.status !== 'running';

const runningReferenceOf = (attempt: RunAttemptResponse | undefined): string | undefined =>
  attempt?.status === 'running' ? attempt.reference : undefined;

const noticeOf = (attempt: RunAttemptResponse | undefined): WatchedRunNotice | undefined => {
  if (attempt === undefined) return undefined;
  const { forced } = attempt;
  if (attempt.status === 'running') {
    return attempt.overrunning ? { kind: 'overrunning', startedAt: attempt.startedAt, forced } : undefined;
  }

  return attempt.report === null ? { kind: 'unfinished', forced } : { kind: 'result', report: attempt.report, forced };
};

export function useDetectionRun(enabled: boolean): DetectionRun {
  const dispatch = useDispatch();
  const [startRun, started] = useStartDetectionRunMutation();
  const { data: latest, refetch: rereadLatest } = useGetLatestRunAttemptQuery(undefined, {
    skip: !enabled,
    refetchOnMountOrArgChange: true,
  });
  const [refusal, setRefusal] = useState<RequestError>();
  const [inProgressElsewhere, setInProgressElsewhere] = useState<string>();
  const watched = started.data?.reference ?? inProgressElsewhere ?? runningReferenceOf(latest);
  const { currentData: known } = runsApi.endpoints.getRunAttempt.useQueryState(watched ?? skipToken);
  const { currentData: attempt } = useGetRunAttemptQuery(watched ?? skipToken, {
    pollingInterval: isFinished(known) ? NOT_POLLING : RUN_POLLING_INTERVAL_MS,
  });
  const finished = isFinished(attempt);

  const notice: WatchedRunNotice | undefined =
    refusal === undefined ? noticeOf(attempt) : { kind: 'refused', error: refusal };
  const refreshedReference = notice?.kind === 'result' ? attempt?.reference : undefined;

  useEffect(() => {
    if (refreshedReference === undefined) return;
    dispatch(api.util.invalidateTags([{ type: 'Incident', id: 'LIST' }]));
  }, [dispatch, refreshedReference]);

  const startWatchingRun = async (request: StartDetectionRunRequest): Promise<void> => {
    setRefusal(undefined);
    setInProgressElsewhere(undefined);
    const answer = await startRun(request);
    if (!('error' in answer)) return;

    const inProgress = isConflict(answer.error) ? (await rereadLatest()).data?.reference : undefined;
    if (inProgress === undefined) {
      setRefusal(answer.error);
      return;
    }

    setInProgressElsewhere(inProgress);
  };

  return {
    running: started.isLoading || (watched !== undefined && !finished),
    notice,
    start: (request) => {
      void startWatchingRun(request);
    },
  };
}
